import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { fetchLeaveRequests, fetchAgentList } from '../hooks/queries';
import { ILeaveRequest } from '../types/leave';
import { useNotifState } from '../state/notifs';
import TableWithSearchCTA from '../components/TableWithSearchCTA';
import SideModal from '../components/common/SideModal';
import LeaveRequestDetails from '../components/LeaveRequestDetails';
import { useLeaveRequestsCol } from '../components/columns/useLeaveRequestsCol';

const LeaveRequests = () => {
  const queryClient = useQueryClient();
  const { setShowNotification } = useNotifState();

  const [selectedLeaveRequest, setSelectedLeaveRequest] =
    useState<ILeaveRequest>();

  const {
    data: leaveRequestsData,
    isLoading: isLoadingLeaveRequests,
    isFetching: isFetchingLeaveRequests,
    refetch: refetchLeaveRequests,
    error,
  } = fetchLeaveRequests();

  const {
    data: agents,
    isLoading: isLoadingAgents,
    isFetching: isFetchingAgents,
  } = fetchAgentList();

  const columns = useLeaveRequestsCol({
    onView: (data) => setSelectedLeaveRequest(data),
    agents: agents?.agents ?? [],
  });

  const closeLeaveModal = () => setSelectedLeaveRequest(undefined);

  const onLeaveRequestUpdate = () => {
    queryClient.invalidateQueries({
      queryKey: ['leave-requests'] as any,
    });

    refetchLeaveRequests()
      .catch(() => toast.error('Error refetching leave requests'))
      .finally(closeLeaveModal);
  };

  useEffect(() => {
    setShowNotification(false);
  }, []);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'request-for-info':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 border border-blue-200';
    }
  };

  return (
    <div
      className="w-full flex flex-col"
      style={{ height: 'calc(100vh - 3rem)' }}
    >
      <TableWithSearchCTA
        columnDefs={columns}
        rowData={leaveRequestsData?.data?.leaveRequests ?? []}
        isLoading={isLoadingLeaveRequests || isLoadingAgents}
        isFetching={isFetchingLeaveRequests || isFetchingAgents}
        error={error as Error}
        refetch={refetchLeaveRequests}
        headerTitle="Leave Requests"
        enableRefresh
        storageKey="leave_requests_table"
      />
      <SideModal
        title={
          <h2 className="text-lg font-semibold flex items-center">
            Leave Request
            <span className="ml-2">
              <span
                className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(selectedLeaveRequest?.status)}`}
              >
                {selectedLeaveRequest?.status}
              </span>
            </span>
          </h2>
        }
        isOpen={!!selectedLeaveRequest}
        onClose={closeLeaveModal}
      >
        <LeaveRequestDetails
          leaveRequest={selectedLeaveRequest}
          onUpdate={() => {
            toast.success('Leave request updated successfully!');
            onLeaveRequestUpdate();
          }}
          onUpdateError={() => toast.error('Error updating leave request')}
        />
      </SideModal>
    </div>
  );
};

export default LeaveRequests;
